import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from "@nexus/ui"
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard"
import { hasAdLibraryUrl, stripAdIdPrefix } from "../../lib/drilldown-row"

export interface DrilldownCreativeLinkMenuProps {
  /** Direct GCS asset URL of the visible creative (or carousel slide). */
  assetUrl: string
  /** Single-ad scope; null when the card rolls up several ads. */
  adId: string | null
  /** Creative hash. */
  creativeId: string
  adLibUrl: string
}

export function DrilldownCreativeLinkMenu({
  assetUrl,
  adId,
  creativeId,
  adLibUrl,
}: DrilldownCreativeLinkMenuProps) {
  const { t } = useTranslation()
  const { copy } = useCopyToClipboard()
  const hasAdLibUrl = hasAdLibraryUrl(adLibUrl)

  const copyWithToast = useCallback(
    async (value: string, successMessage: string) => {
      const isCopied = await copy(value)
      if (isCopied) {
        toast.success(successMessage)
      } else {
        toast.error(t("drilldown.drawer.linkMenu.copyError", "Couldn't copy to clipboard"))
      }
    },
    [copy, t],
  )

  const handleCopyLink = useCallback(() => {
    void copyWithToast(assetUrl, t("drilldown.drawer.linkMenu.linkCopied", "Link copied to clipboard"))
  }, [assetUrl, copyWithToast, t])

  const handleCopyAdId = useCallback(() => {
    if (!adId) return
    void copyWithToast(
      stripAdIdPrefix(adId),
      t("drilldown.drawer.linkMenu.adIdCopied", "Ad ID copied to clipboard"),
    )
  }, [adId, copyWithToast, t])

  const handleCopyCreativeId = useCallback(() => {
    void copyWithToast(
      creativeId,
      t("drilldown.drawer.linkMenu.creativeIdCopied", "Creative ID copied to clipboard"),
    )
  }, [creativeId, copyWithToast, t])

  const handleOpenAdLibrary = useCallback(() => {
    window.open(adLibUrl, "_blank", "noopener,noreferrer")
  }, [adLibUrl])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex size-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:text-foreground data-[state=open]:text-foreground"
          aria-label={t("drilldown.drawer.linkMenu.trigger", "Copy links and IDs")}
        >
          <Icon name="link" size="xs" color="inherit" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-52">
        <DropdownMenuItem icon="link" disabled={!assetUrl} onSelect={handleCopyLink}>
          {t("drilldown.drawer.linkMenu.copyLink", "Copy link")}
        </DropdownMenuItem>
        <DropdownMenuItem icon="copy" disabled={!adId} onSelect={handleCopyAdId}>
          {t("drilldown.drawer.linkMenu.copyAdId", "Copy Ad ID")}
          {adId ? null : (
            <span className="ml-auto text-[11px] text-muted-foreground">
              {t("drilldown.drawer.linkMenu.multipleAds", "Multiple ads")}
            </span>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem icon="copy" disabled={!creativeId} onSelect={handleCopyCreativeId}>
          {t("drilldown.drawer.linkMenu.copyCreativeId", "Copy Creative ID")}
        </DropdownMenuItem>
        {hasAdLibUrl ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem icon="external-link" onSelect={handleOpenAdLibrary}>
              {t("drilldown.drawer.linkMenu.seeInAdLibrary", "See in Ad Library")}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
